import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatSort, Sort } from '@angular/material/sort';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, map } from 'rxjs/operators';

// SERVICES
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';

import { LocationApiService } from './../../../shared/services/api/location/location-api.service';

@Component({
  selector: 'lum-location-warehouse-selection',
  templateUrl: './location-warehouse-selection.component.html',
  styleUrls: ['./location-warehouse-selection.component.scss'],
})
export class LocationWarehouseSelectionComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: true }) sort :MatSort;

  sku :any; // TODO: The v1 api still using snake case for the response cannot use IRfq interface
  loading :boolean = false;
  locationStocks :any = [];
  selectedItemKeys :number[] = []; // An array to store the IDs of selectedItemKeys location stocks

  private onDestroy$ = new Subject();

  constructor(
    private route :ActivatedRoute,
    private rfqApiService :RfqApiService,
    private cd :ChangeDetectorRef,
    private router :Router,
    private dialog :MatDialog,
    private locationApiService :LocationApiService,
  ) {}

  ngOnInit() :void {
    this.getRouterQueryParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  // Toggles all checkboxes based on the "Select All" checkbox state
  toggleAll(checked :boolean) {
    if (checked) {
      this.selectedItemKeys = this.locationStocks.map((ls :any) => ls.key);
    } else {
      this.selectedItemKeys = [];
    }
  }

  // Checks if all checkboxes are selected
  isAllSelected() {
    return this.selectedItemKeys.length === this.locationStocks.length;
  }

  // Toggles a single checkbox's selection state
  toggleSelection(key :number, event :MatCheckboxChange) {
    const index = this.selectedItemKeys.findIndex((i) => i === key);
    if (index === -1) {
      this.selectedItemKeys.push(key);
    } else {
      this.selectedItemKeys.splice(index, 1);
    }
  }

  navigateToEntry(data :any, event :MouseEvent) {
    // Check if the clicked element is NOT a checkbox.
    // The classList contains 'mat-checkbox' if the clicked element was inside a mat-checkbox.
    if (!(event.target as HTMLElement).classList.contains('mat-checkbox')) {
      this.router.navigate(['/warehouse-mobile-app/quantity-entry', data.pivotId]);
    }
  }

  openTransferDialog() :void {
    this.router.navigate(['/warehouse-mobile-app/transfer/bulk']);
    this.locationApiService.setSelectedSkuLocations({
      sku: this.sku,
      selectedWarehouses: this.selectedWarehouses,
    });
  }

  onSort(sort :Sort) :void {
    const data = this.locationStocks.slice();

    if (!sort.active || sort.direction === '') {
      this.locationStocks = data;

      return;
    }

    this.locationStocks = data.sort((a :any, b :any) => {
      const isAsc = sort.direction === 'asc';

      switch (sort.active) {
        case 'warehouseName':
        case 'location':
        case 'lotNumber':
        case 'productionDate':
        case 'quantity':
          return this.compareValue(a[sort.active], b[sort.active], isAsc);
        default:
          return 0;
      }
    });
  }

  private compareValue(a :number | string, b :number | string, isAsc :boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  private getRouterQueryParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        const id = params['sku'];
        this.loading = true;
        if (id) {
          this.getRfq(id);
        }
      }),

      takeUntil(this.onDestroy$),
    );
  }

  private getRfq(id :number) :void {
    this.rfqApiService
      .getById(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          this.sku = data;
          this.loading = false;
          this.setLocationStocks();
          this.cd.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.cd.detectChanges();

          return error;
        },
      });
  }

  private setLocationStocks() :void {
    if (!this.sku.location_stocks) {
      this.locationStocks = [];
    }

    this.locationStocks = this.sku.location_stocks.flatMap(
      (stock :any, stockKey :number) => {
        const { id, warehouse } = stock;
        const { location_name } = warehouse;

        return stock.stock_locations.map((location :any, locationKey :number) => {
          const { location_name: locationName, pivot, _id: locationId } = location;
          const { id: pivotId, lot, onhand_quantity: quantity } = pivot;
          const { lot_number: lotNumber, production_date: productionDate } =
            lot || {};

          return {
            id,
            key: locationKey + stockKey * 10,
            warehouseId: warehouse.id,
            warehouseName: location_name,
            pivotId,
            locationId: location.id,
            location: locationName,
            lotNumber,
            productionDate,
            quantity,
          };
        });
      },
    );
  }

  get selectedLocations() :any[] {
    return this.locationStocks.filter((stock :any) =>
      this.selectedItemKeys.includes(stock.pivot.location_id),
    );
  }

  get selectedWarehouses() :any[] {
    return this.locationStocks.filter((ls :any) =>
      this.selectedItemKeys.includes(ls.key),
    );
  }
}
